/**
 * Queue domain types.
 *
 * Types mirror the API contract from the unified approval queue API.
 * The `ApprovalItem` shape normalizes both agent tool approvals and automation
 * execution approvals into a single frontend-friendly shape.
 *
 * Ported from Bridge with adaptations for Stream's Tauri-based architecture.
 */

import * as tokens from '@/lib/tokens'



/**
 * Sources of approval items.
 */
export type ApprovalSource = 'agent_tool' | 'automation'

/**
 * Approval item status.
 */
export type ApprovalStatus = 'pending' | 'claimed' | 'resolved'

/**
 * Resolution outcomes for history items.
 */
export type ApprovalResolution = 'approved' | 'rejected' | 'suppressed'

/**
 * Unified approval item shape.
 *
 * Both agent tool approvals and automation executions project into this shape.
 * The `id` is a composite key prefixed by source (`at_` for agent tool,
 * `am_` for automation).
 */
export interface ApprovalItem {
  id: string
  source: ApprovalSource
  source_ref: string
  tool_name: string
  agent_id: string
  display_name: string
  icon: string
  color: string
  created_at: string
  trigger_summary: string
  breadcrumb: string
  owner_id: string | null
  blocking: boolean
  status: ApprovalStatus
  resolved_at: string | null
  resolved_by: string | null
  resolution: ApprovalResolution | null
}

/**
 * Expanded detail response for a single approval item.
 * The card payload is consumed by the approval card renderer.
 */
export interface ApprovalDetailResponse {
  item: ApprovalItem
  card: {
    tool_name: string
    display: {
      name: string
      icon: string
      color: string
    }
    /**
     * Human-readable name of the app this approval acts on (e.g. "My
     * Timetable"), derived from the tool-call `args.app_id`. Surfaced as a
     * chip in the approval-detail header so the operator sees which app a
     * review reply (etc.) targets without expanding Tool Arguments. Absent
     * for tools that don't act on a specific app.
     */
    app_label?: string | null
    sections: ApprovalCardSection[]
    side_effects: string[]
    quick_reject_reasons: QuickRejectReason[]
    /** Default reject reasons from the registry (shown alongside tool-specific). */
    default_quick_reject_reasons?: QuickRejectReason[]
    supports_edit: boolean
    editable_fields: string[]
    /**
     * Per-tool overrides for the action-button row in the approval inspector.
     * When absent the frontend renders the standard Approve / Reject layout.
     */
    button_config?: ToolButtonConfig
  }
  registry_version: string
}

/**
 * A section in the approval card layout.
 */
export interface ApprovalCardSection {
  id: string
  label: string
  renderer: string
  collapsed: boolean
  data: unknown
  error: string | null
}

/**
 * A single key/value field shown in a {@link SourceContext} header
 * (e.g. an email's To/From/Subject, or a review's rating).
 */
export interface SourceContextField {
  label: string
  value: string
}

/**
 * The original item an operator is responding to, surfaced read-only inside the
 * rejection / response modal so they never have to leave the dialog (or scroll
 * the card behind it) to see what is being acted on (Task 4074b).
 *
 * Built from the approval card's source section — `original_review` for review
 * replies, `original_email` for support-email tool calls — by
 * `buildSourceContext`. `body` is always PLAIN TEXT so it can flow through the
 * card-level translate control (HTML is stripped before it reaches here).
 */
export interface SourceContext {
  /** Heading for the block, e.g. "Original Review" / "Original Email". */
  label: string
  /** The quoted source text (plain text), shown above the feedback area. */
  body: string
  /**
   * Optional compact key/value rows shown above the body — rating/reviewer for
   * reviews, From/Subject for emails.
   */
  fields?: SourceContextField[]
  /**
   * Optional detected/known source-language hint. Currently informational; the
   * translate control auto-detects, so this is reserved for future use.
   */
  language?: string
}

/**
 * Quick reject reason option.
 */
export interface QuickRejectReason {
  id: string
  label: string
  /**
   * The text inserted into the rejection message textarea when this reason is selected.
   * Defaults to `label` if not provided.
   */
  feedbackText?: string
}

/**
 * Per-tool overrides for the action-button row in the approval inspector.
 *
 * Mirrors `ToolUIButtonConfig` from the Python backend schema. When absent
 * (null/undefined on the card) the frontend renders the default
 * Approve / Reject layout unchanged.
 *
 * Used by `AttentionInspector` to conditionally hide Approve and relabel
 * Reject, and by `RejectDialog` to customise its title / description / submit
 * label for communication-style tools (e.g. `escalate_to_operator`).
 */
export interface ToolButtonConfig {
  /**
   * When true the Approve button is hidden entirely.
   * Suitable for tools where "approve" has no meaningful operator action.
   */
  hide_approve?: boolean
  /**
   * Label for the primary negative-action button (default: "Reject").
   * Override to "Respond" for communication tools so the affordance is clear.
   */
  reject_label?: string
  /** Title shown at the top of the reject / respond dialog. */
  reject_dialog_title?: string
  /** Subtitle / description line beneath the dialog title. */
  reject_dialog_description?: string
  /** Label on the dialog's primary submit button (default: "Reject"). */
  reject_submit_label?: string
  /**
   * Label for an optional suppress/dismiss button shown alongside Reject.
   *
   * When absent/undefined no suppress button is rendered — preserving the
   * standard two-button (Approve / Reject) layout. When set to a string
   * (e.g. "Dismiss") a button with that label is surfaced; clicking it
   * submits a *suppressed* resolution without opening the feedback dialog.
   *
   * For `escalate_to_operator` this is "Dismiss" so the operator can either
   * respond with guidance (Respond → rejected+feedback) or quietly dismiss
   * (Dismiss → suppressed).
   */
  suppress_label?: string
}

/**
 * Predefined time periods for history filter.
 */
export type HistoryPeriod = '7d' | '30d' | 'all' | 'custom'

/**
 * Secondary filter state for queue views.
 */
export interface QueueFiltersState {
  tool?: string
  agent?: string
  blockingOnly?: boolean
  search?: string
  // History-specific filters
  period?: HistoryPeriod
  operator?: string
  outcome?: ApprovalResolution | 'all'
  since?: string
  until?: string
}

/**
 * Rejection feedback structure.
 */
export interface RejectFeedback {
  quick_reasons: string[]
  feedback: string
}


// ============================================
// Attention Types (Unified /v1/attention)
// ============================================

/**
 * Attention type discriminator.
 *
 * These match the Go operations service attention_type values and the
 * admin-portal's Dart AttentionItem model.
 */
export type AttentionType =
  | 'workflow_step_approval'
  | 'workflow_human_review'
  | 'agent_tool_approval'
  | 'agent_text_review'
  | 'workflow_agent_approval'
  | 'pr_review'

/**
 * Unified attention item from GET /v1/attention.
 *
 * Mirrors the Go operations service AttentionItem model. This is the
 * primary data type for the queue item list — it replaces ApprovalItem
 * as the list data source while ApprovalItem is still used for detail/card.
 */
export interface AttentionItem {
  id: string
  /**
   * Canonical globally-unique attention id (`attn_…`) shared verbatim by the
   * operations and agents services (Task 4065a contract). Per the contract,
   * `id === attention_id` — the backend `list` response (`ToQueueView`) maps
   * `id` to the canonical id, and the queue stays keyed by `item.id`.
   *
   * Surfaced to the operator as a copyable badge on the row and inspector
   * (Task 4068a). Optional on the wire so this is compatible with BOTH the
   * legacy `list` shape (which omits it) and the new canonical shape (which
   * sends it). Read via `getAttentionId(item)` so callers always get a value
   * (falls back to `id`).
   */
  attention_id?: string
  source: 'workflow' | 'agent'
  attention_type: AttentionType
  title: string
  subtitle: string
  created_at: string
  execution_id: string | null
  agent_id: string | null
  session_id: string | null
  customer_name: string | null
  customer_email: string | null
  agent_name: string | null
  /**
   * Status of the backing AutomationExecution (Task 4054f). Emitted by the
   * operations /v1/attention serializer whenever the item has a resolvable
   * execution row (workflow items, hybrid items, enriched orphan agent
   * items). Absent/undefined for pure agent items with no execution and for
   * payloads from older backends — treat "unknown" as permissive when
   * gating actions on it.
   */
  execution_status?: string | null
  details: Record<string, unknown> | null
  // Resolution fields — populated only for history items.
  resolution: string | null
  resolved_by: string | null
  resolved_at: string | null
  // Claim / ownership fields (Task 3201a). When set, claimed_by is one of
  // the four OperatorTag values. Items remain visible to all operators
  // regardless of who owns them.
  claimed_by?: OperatorTag | null
  claimed_at?: string | null
}

/**
 * Response from GET /v1/attention.
 */
export interface AttentionListResponse {
  items: AttentionItem[]
  count: number
}

/**
 * Response from GET /v1/attention/count.
 */
export interface AttentionCountResponse {
  count: number
  hash: string
}

/**
 * Response from GET /v1/attention/history.
 */
export interface AttentionHistoryResponse {
  items: AttentionItem[]
  count: number
  has_more: boolean
  /** Opaque cursor to pass as `cursor` on the next page request. */
  next_cursor?: string
}

/**
 * Known operator tags for assignment.
 */
export type OperatorTag = 'MJT' | 'BH' | 'JW' | 'TF' | 'MAL'

/**
 * All known operator tags.
 */
export const OPERATOR_TAGS: OperatorTag[] = ['MJT', 'BH', 'JW', 'TF', 'MAL']

/**
 * Queue tab filter for the unified attention-based queue.
 */
export type AttentionQueueTab = 'all' | 'mine' | 'history'

/**
 * Top-level queue view — RETAINED FOR ROUTING BACK-COMPAT ONLY (Task 4072).
 *
 * The Queue is a SINGLE unified list: every pending item (tool approvals,
 * workflow human reviews, agent text reviews, PR reviews) appears together,
 * regardless of source, per docs/architecture/UNIFIED_ATTENTION_INBOX.md §2/§7.
 * Item *type* drives the card affordance (approve/reject vs read-and-reply) and
 * an OPTIONAL, non-exclusive filter (see QueueTypeFilter), but NEVER partitions
 * the list into separate views.
 *
 * This type used to split the page into two mutually exclusive 'attention' vs
 * 'responses' lists; that split was removed in Task 4072. The value now only
 * survives as the `:view` URL segment (`/queue/{view}/{tab}`) so existing deep
 * links keep resolving — it no longer filters the list. New code should not
 * branch on it.
 */
export type QueueView = 'attention' | 'responses'

/**
 * Optional, non-exclusive type filter for the unified pending list (Task 4072).
 *
 * Default is 'all' (everything visible — the unified list). The operator may
 * narrow to just approvals or just reviews as a convenience; this is a view
 * preference, NOT a list partition, and resets to 'all' on reload.
 *   - 'all'        — show every type (default).
 *   - 'approvals'  — tool approvals + PR reviews (approve/reject cards).
 *   - 'reviews'    — workflow human reviews + agent text reviews (read-and-reply).
 */
export type QueueTypeFilter = 'all' | 'approvals' | 'reviews'

/**
 * Whether an attention item of the given type passes the optional pending
 * type filter. 'all' always passes (the default unified list).
 */
export function matchesTypeFilter(type: AttentionType, filter: QueueTypeFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'approvals') return isToolApproval(type) || isPRReview(type)
  // 'reviews'
  return isTextReview(type)
}

/**
 * Response from GET /v1/attention/assignments.
 */
export interface AttentionAssignmentsResponse {
  assignments: Record<string, string>
}

// ============================================
// Attention Helpers
// ============================================

/**
 * Whether an attention type is a tool approval (uses card rendering).
 */
export function isToolApproval(type: AttentionType): boolean {
  return (
    type === 'workflow_step_approval' ||
    type === 'agent_tool_approval' ||
    type === 'workflow_agent_approval'
  )
}

/**
 * Whether an attention type is a text review (uses review/reply panel).
 */
export function isTextReview(type: AttentionType): boolean {
  return type === 'workflow_human_review' || type === 'agent_text_review'
}

/**
 * Extract the review_type sub-discriminator from an attention item's details.
 * Used to dispatch specialised renderers (e.g. DebugReviewPanel) for items
 * that share the same attention_type but have distinct review_type payloads.
 *
 * The backend stores review_type inside details.proposed_resolution.
 *
 * Returns null if no review_type is present or unrecognised.
 */
export type ReviewSubtype = 'debug_review'

export function getReviewSubtype(item: AttentionItem): ReviewSubtype | null {
  const proposedResolution = item.details?.proposed_resolution as Record<string, unknown> | undefined
  const reviewType = proposedResolution?.review_type
  if (reviewType === 'debug_review') return 'debug_review'
  return null
}

/**
 * Whether an attention type is a PR review.
 */
export function isPRReview(type: AttentionType): boolean {
  return type === 'pr_review'
}

/**
 * Human-readable label for the attention type.
 * Matches the admin-portal's typeLabel pattern.
 */
export function getAttentionTypeLabel(type: AttentionType): string {
  switch (type) {
    case 'workflow_step_approval':
    case 'agent_tool_approval':
    case 'workflow_agent_approval':
      return 'Tool Approval'
    case 'workflow_human_review':
    case 'agent_text_review':
      return 'Review & Reply'
    case 'pr_review':
      return 'PR Review'
    default:
      return 'Unknown'
  }
}

/**
 * Badge color for the attention type.
 * Returns resolved hex values from the shared token map.
 */
export function getAttentionTypeBadgeColor(type: AttentionType): string {
  switch (type) {
    case 'workflow_step_approval':
    case 'agent_tool_approval':
    case 'workflow_agent_approval':
      return tokens.warning
    case 'workflow_human_review':
    case 'agent_text_review':
      return tokens.accentPrimary
    case 'pr_review':
      return tokens.categoryCommunication
    default:
      return tokens.textMuted
  }
}

/**
 * Best-effort customer display for an attention item.
 * Falls back through: name → email → null.
 */
export function getCustomerDisplay(item: AttentionItem): string | null {
  if (item.customer_name) return item.customer_name
  if (item.customer_email) return item.customer_email
  return null
}

/**
 * Return the canonical attention id for display/copy (Task 4068a).
 *
 * Per the 4065a contract `id === attention_id`. The backend may serialize the
 * canonical id either under the explicit `attention_id` field (new shape) or
 * only as `id` (legacy shape / older backends). Reading through this helper
 * makes the UI wire-compatible with both: it prefers `attention_id` when
 * present and falls back to `id`, which is the same value by contract.
 */
export function getAttentionId(item: Pick<AttentionItem, 'id' | 'attention_id'>): string {
  return item.attention_id ?? item.id
}
